import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AGQueueAttentionLevel } from '@core/enumeration';
import { IAppealsAndGrievancesQueue, IAppealsAndGrievancesQueueItem } from '@core/model';
import { AppealsAndGrievancesService, AuthenticationService, ProductService } from '@core/service';
import { AppealsAndGrievancesProductPath, PortalProducts } from '@shared/enumeration';
import { IQueue } from '@shared/model';
import { ArrayUtilities, StringUtilities } from '@shared/utility';
import { ODataService, TableColumn } from '@mynexus/mynexus-ui-lib';
import { ConfirmationService, LazyLoadEvent, MessageService, SelectItem } from 'primeng/api';
import { Table } from 'primeng/table';
import { finalize } from 'rxjs/operators';
import { SubSink } from 'subsink';

interface ExpandedLazyLoadEvent extends LazyLoadEvent {
  selection?: IQueue[];
}

@Component({
    selector: 'app-appeals-and-grievances-queue',
    templateUrl: './appeals-and-grievances-queue.component.html',
    styleUrls: ['./appeals-and-grievances-queue.component.scss'],
    standalone: false
})
export class AppealsAndGrievancesQueueComponent implements OnInit, OnDestroy {
  @ViewChild('dt', { static: true }) dataTable: Table;
  selectedQueues: IAppealsAndGrievancesQueueItem[] = [];

  readonly amountOfRows = 25;
  readonly defaultSortField = 'dueDateTime';
  readonly defaultSortOrder = 1;
  readonly filterStoreName = 'A&GQueueFilters';
  readonly defaultFilters: ExpandedLazyLoadEvent = {
    first: 0,
    rows: this.amountOfRows,
    sortField: this.defaultSortField,
    sortOrder: 1,
    filters: null,
    globalFilter: null
  };

  cols: Array<TableColumn>;
  isLoading = false;
  totalRecords = 0;
  pageResults: IAppealsAndGrievancesQueueItem[] = [];
  sortField: string = null;
  topCheckboxValue = false;
  prevSelectedQueueLength: IAppealsAndGrievancesQueueItem[] = [];
  private subs = new SubSink();

  rangeDates: Date[] = null;
  queueNameList: SelectItem[] = [];
  tenantList: SelectItem[] = [];
  lineOfBusinessList: SelectItem[] = [];
  productsList: SelectItem[] = [];
  urgencyList: SelectItem[] = [];
  serviceProviderList: SelectItem[] = [];
  stateList: SelectItem[] = [];
  agStatusList: SelectItem[] = [];
  assignedToNameList: SelectItem[] = [];

  serviceProviderValue: any[] = [];
  receivedOnRange: Date[] = null;
  dueDateTimeRange: Date[] = null;
  currentDate = new Date().toISOString();

  userName = '';
  notFilteredFields: string[] = ['outcomeSequenceNumber', 'name'];
  selectAllState = false;
  selectedMessage = '';
  appealsAndGrievancesProductPath = AppealsAndGrievancesProductPath;

  get AGQueueAttentionLevel() {
    return AGQueueAttentionLevel;
  }

  constructor(
    private router: Router,
    private oDataService: ODataService,
    private appealsAndGrievancesService: AppealsAndGrievancesService,
    private authenticationService: AuthenticationService,
    private confirmationService: ConfirmationService,
    private productService: ProductService,
    private messageService: MessageService,
  ) { }

  ngOnInit() {
    this.isLoading = true;

    this.cols = [
      new TableColumn('Alerts', 'alertStatus', '180px'),
      new TableColumn('Member', 'subscriberName', '180px'),
      new TableColumn('Auth #', 'authorizationNumber', '170px'),
      new TableColumn('A&G Reference Number', 'referenceNumber', '225px'),
      new TableColumn('Received Date/Time', 'receivedOn', '155px'),
      new TableColumn('Health Plan', 'tenantName', '135px'),
      new TableColumn('Line of Business', 'lineOfBusinessName', '180px'),
      new TableColumn('Program', 'productName', '135px'),
      new TableColumn('Urgency', 'urgency', '135px'),
      new TableColumn('Service Provider', 'serviceProviderName', '180px'),
      new TableColumn('State', 'subscriberState', '110px'),
      new TableColumn('Outcome Number', 'outcomeSequenceNumber', '90px'),
      new TableColumn('A&G Status', 'status', '140px'),
      new TableColumn('Due Date/Time', 'dueDateTime', '200px'),
      new TableColumn('Internal Owner', 'assignedToName', '160px')
    ] as Array<TableColumn>;

    this.getUsersCredentials();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  getUsersCredentials() {
    this.userName = this.authenticationService.userName;
  }

  setFiltersFromStore() {
    const storage = this.dataTable.getStorage();
    const state = JSON.parse(storage.getItem(this.filterStoreName));
    if (state && state.filters) {
      const { receivedOn, dueDateTime, serviceProviderNPI } = state.filters;
      if (receivedOn && receivedOn.value[0]) {
        this.receivedOnRange = [new Date(receivedOn.value[0])];
        if (receivedOn.value[1]) {
          this.receivedOnRange.push(new Date(receivedOn.value[1]));
        }
      }
      if (dueDateTime && dueDateTime.value[0]) {
        this.dueDateTimeRange = [new Date(dueDateTime.value[0])];
        if (dueDateTime.value[1]) {
          this.dueDateTimeRange.push(new Date(dueDateTime.value[1]));
        }
      }
      if (serviceProviderNPI) {
        this.serviceProviderValue = this.serviceProviderList
          .filter(item => {
            return serviceProviderNPI.value.includes(item.value.npi);
          })
          .map(item => item.value);
      }
    }
  }

  clearSelected() {
    this.selectedQueues = [];
    this.topCheckboxValue = false;
    this.dataTable.selection = [];
    const storage = this.dataTable.getStorage();
    const state = JSON.parse(storage.getItem(this.filterStoreName));
    if (state) {
      storage.setItem(this.filterStoreName, JSON.stringify({ ...state, selection: this.selectedQueues }));
    }
  }

  reloadQueue() {
    const storage = this.dataTable.getStorage();
    const state = JSON.parse(storage.getItem(this.filterStoreName));
    this.loadData(state || this.defaultFilters);
  }

  clearAll() {
    const storage = this.dataTable.getStorage();
    storage.setItem(this.filterStoreName, JSON.stringify(this.defaultFilters));
    this.dataTable.lazy = false;
    this.dataTable.clear();
    this.dataTable.filters = {};
    this.dataTable.totalRecords = this.totalRecords;
    this.dataTable.lazy = true;
    this.serviceProviderValue = [];
    this.receivedOnRange = null;
    this.dueDateTimeRange = null;

    this.dataTable.sort({ field: this.defaultSortField });
  }

  mapFilters(event: LazyLoadEvent): LazyLoadEvent {
    const { dueDateTime, receivedOn } = event.filters || {};
    let dueDateTimeFilter;
    let receivedFilters;

    if (dueDateTime) {
      let [startDate, endDate] = dueDateTime.value;
      dueDateTimeFilter = { ...dueDateTime };
      endDate = endDate || startDate;

      endDate = new Date(endDate);
      endDate.setHours(23,59,59,0);

      startDate = new Date(startDate);
      startDate.setHours(0,0,0,0);

      dueDateTimeFilter.value = [startDate, endDate];
    }

    if (receivedOn) {
      receivedFilters = { ...receivedOn };

      const endDate = new Date(receivedFilters.value[1] || receivedFilters.value[0]);
      endDate.setHours(23,59,59,0);

      receivedFilters.value = [receivedFilters.value[0], endDate];
    }

    return {
      ...event,
      filters: {
        ...event.filters,
        ...(dueDateTime && { dueDateTime: dueDateTimeFilter }),
        ...(receivedOn && { receivedOn: receivedFilters }),
      },
    };
  }

  loadData(filters: ExpandedLazyLoadEvent) {
    this.isLoading = true;
    if (!filters.sortField) {
      filters.sortField = this.defaultSortField;
    }

    this.dataTable.sortField = filters.sortField;
    filters.multiSortMeta = filters.sortField === 'dueDateTime'
      ? [{ field: 'dueDateTimePriority', order: filters.sortOrder }, { field: 'dueDateTime', order: filters.sortOrder }]
      : [];

    const mappedFilters = this.mapFilters(filters);
    const queryParams = this.oDataService.buildQuery(mappedFilters);

    this.subs.sink = this.appealsAndGrievancesService.getQueueList(queryParams)
      .pipe()
      .subscribe((res: IAppealsAndGrievancesQueue) => {
         if (res.relevantData && res.result) {
          this.clearSelected();
          this.pageResults = res.result.items.map((queueItem: IAppealsAndGrievancesQueueItem) => {
            queueItem.dueDateDaysHours = new Date(queueItem.dueDateTime).getTime() - new Date().getTime() + 20000; // shift for label 20s
            queueItem.name = 'Appeals and Grievances';
            return queueItem;
          });
          this.totalRecords = res.result.count;
          if (this.pageResults.length == 0 && this.totalRecords > 0 && Object.keys(filters.filters).length === 0) {
            this.isLoading = true;
            filters.first = filters.first +  this.amountOfRows;
            this.loadData(filters);
          } else {
            this.isLoading = false;
          }
          const dropDownValues = res.relevantData;
          this.queueNameList = ArrayUtilities.arrayToSelectItemArrayByItemWithNull(['Appeals and Grievances']);
          this.tenantList = ArrayUtilities.arrayToSelectItemArrayByItemWithNull(dropDownValues.tenantNames);
          this.lineOfBusinessList = ArrayUtilities.arrayToSelectItemArrayByKeyAndValue(dropDownValues.lineOfBusiness, 'lineOfBusinessId', 'description', false);
          this.productsList = ArrayUtilities.arrayToSelectItemArrayByKeyAndValue(dropDownValues.product, 'productId', 'shortName', false);
          this.urgencyList = ArrayUtilities.arrayToSelectItemArrayByItemWithNull(dropDownValues.urgencies);
          this.serviceProviderList = ArrayUtilities.arraySelectItemArrayByKeyAndValueNull(
             ArrayUtilities.uniqueObjectByKey(dropDownValues.serviceProviders, 'npi', 'name'),
             'name',
             'npi'
           );
          this.stateList = ArrayUtilities.arrayToSelectItemArrayByItemWithNull(dropDownValues.subscriberStates);
          this.assignedToNameList = ArrayUtilities.arrayToSelectItemArrayByItemWithNull(dropDownValues.assigneeNames);
          this.agStatusList = [...dropDownValues.statuses]
            .sort((a, b) => a === null ? -1 : b === null ? 1 : a.toString().localeCompare(b))
            .map((item) => {
              let selectItem;
              if (item === '') {
                selectItem = { value: ' ', label: 'N/A' };
              } else if (item === null) {
                selectItem = { value: 'null', label: 'Null' };
              } else {
                selectItem = { value: item, label: item + '' };
              }
              return selectItem;
            });

          this.setFiltersFromStore();
        }
      });
  }

  onSelectionChange() {
    this.topCheckboxValue = this.selectedQueues.length === this.prevSelectedQueueLength.length && this.selectedQueues.length !== 0;
    this.selectedMessage = this.getSelectedMessage(this.selectedQueues.length);
  }

  getSelectedMessage(length: number) {
    const queueText = StringUtilities.pluralize(length, 'queue');
    return `${length} ${queueText} selected`;
  }

  getActiveCheckboxes(): IAppealsAndGrievancesQueueItem[] {
    return this.pageResults.filter(value => value.assignedToName === this.userName);
  }

  selectAllCheckbox() {
    if (this.topCheckboxValue) {
      this.selectedQueues = this.getActiveCheckboxes();
      this.prevSelectedQueueLength = this.selectedQueues;
    } else {
      this.selectedQueues = [];
    }
    this.selectedMessage = this.getSelectedMessage(this.selectedQueues.length);

    const storage = this.dataTable.getStorage();
    const state = JSON.parse(storage.getItem(this.filterStoreName));
    if (state) {
      storage.setItem(this.filterStoreName, JSON.stringify({ ...state, selection: this.selectedQueues }));
    } else {
      storage.setItem(this.filterStoreName, JSON.stringify({ ...this.defaultFilters, selection: this.selectedQueues }));
    }
  }

  filterTable(value, criteria, matchMode) {
    this.selectedQueues = [];
    this.dataTable.filter(value, criteria, matchMode);
  }

  filterServiceProvider(event) {
    this.selectedQueues = [];
    this.dataTable.filter(event.value.map(value => value.npi), 'serviceProviderNPI', 'in');
  }

  isEscalatedCase(queue: IAppealsAndGrievancesQueueItem) {
    return queue.isEscalatedCase ? 'escalated-queue' : null;
  }

  displayAppealsAndGrievanceDetail(event, queue: IAppealsAndGrievancesQueueItem) {
    event.preventDefault();
    this.appealsAndGrievancesService.appealsAndGrievanceDetail = queue.appealAndGrievanceId;
    this.router.navigate([queue.tenantId, 'member', queue.subscriberNumber]);
  }

  displayMemberPage(event, queue: IAppealsAndGrievancesQueueItem) {
    event.preventDefault();
    this.router.navigate(['/', queue.tenantId, 'member', queue.subscriberNumber]);
  }

  async displayAuthPage(event, queue: IAppealsAndGrievancesQueueItem): Promise<void> {
    event.preventDefault();

    const products = await this.productService.getProducts().toPromise();
    const currentProduct = products.find(product => product.productId === queue.productId).shortName;
    const productPath = this.appealsAndGrievancesProductPath[currentProduct];
    const authPath = this.getAuthPath(queue.tenantId, productPath, queue.subscriberNumber, queue.authorizationNumber);

    this.router.navigate(authPath);
  }

  private getAuthPath(tenantId: string, product: string, subscriberNumber: string, authNumber: string): string[] {
    const path = ['/', tenantId];


    path.push(product, 'auth-dashboard', authNumber);

    return path;
  }

  assignQueue(queue: IAppealsAndGrievancesQueueItem) {
    this.subs.sink = this.appealsAndGrievancesService.assignQueue(queue.appealAndGrievanceQueueId)
      .subscribe((res: IAppealsAndGrievancesQueueItem) => {
        if (res) {
          queue.assignedToName = this.authenticationService.userName;
          this.topCheckboxValue = false;
          this.prevSelectedQueueLength = this.getActiveCheckboxes();
        } else {
          this.confirmationService.confirm({
            header: 'Warning',
            message: 'This line has already been assigned. Please refresh your queue view.',
            acceptVisible: false,
            rejectVisible: false,
            accept: () => {
              this.unassignQueue(queue);
            }
          });
        }
      });
  }

  unassignQueue(queue: IAppealsAndGrievancesQueueItem) {
    this.subs.sink = this.appealsAndGrievancesService.unassignQueue(queue.appealAndGrievanceQueueId)
      .subscribe((res: IAppealsAndGrievancesQueueItem) => {
        if (res) {
          queue.assignedToName = '';
          this.prevSelectedQueueLength = this.getActiveCheckboxes();
          this.selectedQueues = this.selectedQueues.filter((queueItem: IAppealsAndGrievancesQueueItem) => {
            return queueItem.appealAndGrievanceQueueId !== queue.appealAndGrievanceQueueId;
          });
          this.selectedMessage = this.getSelectedMessage(this.selectedQueues.length);
          this.reloadQueueFilteredUnAssignOwner();
        }
      });
  }

  reloadQueueFilteredUnAssignOwner(): void {
    const storage = this.dataTable.getStorage();
    const state = JSON.parse(storage.getItem(this.filterStoreName));
    const { filters = {} } = state;

    if (filters.assignedToName?.value?.length) {
      this.reloadQueue();
    }
  }

  assignHandler(queue: IAppealsAndGrievancesQueueItem) {
    if (queue.assignedToName) {
      this.confirmationService.confirm({
        header: 'Confirmation',
        message: 'Are you sure you want to remove this assignment?',
        acceptLabel: 'Yes',
        rejectLabel: 'No',
        accept: () => {
          this.unassignQueue(queue);
        }
      });
    } else {
      this.assignQueue(queue);
    }
  }

  removeFromQueueConfirmation() {
    this.confirmationService.confirm({
      header: 'Confirmation',
      message: 'Are you sure you want to remove these items from the queue?',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      accept: () => {
        const queuesList = this.selectedQueues.map((queue: IAppealsAndGrievancesQueueItem) => queue.appealAndGrievanceQueueId);
        this.removeFromQueue(queuesList);
      }
    });
  }

  removeFromQueue(queues: string[]) {
    this.isLoading = true;
    this.subs.sink = this.appealsAndGrievancesService.deleteQueues(queues)
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe(() => {
        this.reloadQueue();
      }, () => {
        this.messageService.add({
          key: 'appeals-grievances-queue',
          severity: 'error',
          detail: 'The request was unable to successfully remove the queues.',
          life: 5000
        });
      });
  }
}
